import { Color, Spacing } from '@persona-id/design-tokens';
import { DragIcon, PlusIcon, XIcon } from '@persona-id/design-tokens/icons';
import { isEqual, sortBy, startCase } from 'lodash/fp';
import { ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState, VFC } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableChildrenFn,
  DraggableProvided,
  Droppable,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import { StylesConfig } from 'react-select';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import ReactSelect, { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import { reorder } from 'dashboard/lib/dnd/utils';
import { InputSchemaType, TriggerCaseParamSchema } from 'dashboard/models/trigger';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { COUNTRIES, CountryCode, US_STATES } from 'lib/constants/locales';

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[4]};
`;

const ReactSelectStyles: StylesConfig = {
  control: () => ({
    height: 'auto',
  }),
  valueContainer: () => ({
    height: 'auto',
    minHeight: '30px',
    maxHeight: '80px',
    overflowY: 'auto',
    paddingTop: 0,
    paddingBottom: 0,
  }),
  multiValueLabel: () => ({
    padding: '2px',
  }),
  option: () => ({
    paddingTop: `${Spacing[2]}`,
    paddingBottom: `${Spacing[2]}`,
  }),
  indicatorsContainer: (_, state) =>
    state.isMulti
      ? {
          display: 'none',
        }
      : {},
};

const DeleteButton = styled.div`
  margin-left: ${Spacing[2]};
  cursor: pointer;
  // For general browser, show DeleteButton per hovering Row but keep existence for a11y
  opacity: 0;
  &:hover,
  &:focus {
    opacity: 1;
  }
  &:focus {
    color: ${Color.CorePurple800};
  }
`;

const Row = styled.div`
  display: grid;
  gap: ${Spacing[2]};
  grid-template-columns: 1fr auto;
  align-items: center;
  &:hover > ${DeleteButton} {
    opacity: 1;
  }
`;

const StyledInput = styled(Input)`
  height: ${({ theme }) => theme.element.height.xs};
  margin-bottom: ${Spacing[2]};
  padding: 2px ${Spacing[2]};
  width: '256px';
  ${paragraphMixins.md};
`;

const WithLeftSpace = styled.div`
  margin-left: ${Spacing[5]};
`;

const TitleContainer = styled.div`
  display: inline-block;
`;

const BooleanParamContainer = styled('div')`
  display: grid;
  grid-template-columns: auto;
`;

const CheckboxContainer = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing[2]};
  cursor: pointer;
`;

const OptionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  grid-column-gap: ${Spacing[2]};
  align-items: center;
  &:hover > ${DeleteButton} {
    opacity: 1;
  }
`;

const StyledParagraph = styled(Paragraph)`
  white-space: nowrap;
`;

const PropertyContainer = styled.div`
  margin: ${Spacing[2]} 0;
  padding: ${Spacing[4]} ${Spacing[5]};
  background-color: ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const AccordionContainer = styled.div`
  margin: ${Spacing[2]} 0;
  padding: ${Spacing[4]} ${Spacing[5]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const RequirementContainer = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: 1fr 20px;
  row-gap: ${Spacing[2]};
  column-gap: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};
  &:hover > ${DeleteButton} {
    opacity: 1;
  }
`;

export const REACT_SELECT_PORTAL_ID_PREFIX = 'trigger-case-param-schema-editor_';

interface TriggerCaseParamSchemaEditorProps {
  paramSchema: TriggerCaseParamSchema[];
  updateParamSchema: (newSchema: TriggerCaseParamSchema[]) => void;
}

const TriggerCaseParamSchemaEditor: VFC<TriggerCaseParamSchemaEditorProps> = (props) => (
  <Well>
    <InnerParamSchemaEditor {...props} />
  </Well>
);

interface PropertyInputProps {
  index: number;
  label?: string;
  name: string;
  value: string;
  fieldNameErrors?: boolean[];
  updateProperty: (index: number, name: string, value: string) => void;
}

const PropertyInput: VFC<PropertyInputProps> = ({
  index,
  label,
  name,
  value,
  fieldNameErrors,
  updateProperty,
}) => {
  return (
    <>
      {label && <Label color='primary'>{label}</Label>}
      <StyledInput
        aria-label={`Select ${name} for field-${index}`}
        value={value}
        id={`${REACT_SELECT_PORTAL_ID_PREFIX}_${name}-${index}`}
        aria-invalid={fieldNameErrors?.[index]}
        hasError={fieldNameErrors?.[index]}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onChange={(e) => {
          updateProperty(index, name, e.target.value);
        }}
      />
    </>
  );
};

const InnerParamSchemaEditor: VFC<TriggerCaseParamSchemaEditorProps> = ({
  paramSchema,
  updateParamSchema,
}) => {
  // To accept invalid fields (duplicated key, blank key name), use this state instead of given paramSchema props
  // Call updateParamSchema when this state gets valid per update of this state
  const [internalEntries, setInternalEntries] = useState<TriggerCaseParamSchema[]>(paramSchema);

  const isInvalidFieldName = useCallback(
    (name: string) =>
      name.length === 0 ||
      name.includes(' ') ||
      internalEntries.filter((schema) => schema?.name === name).length > 1,
    [internalEntries],
  );

  const fieldNameErrors = useMemo(() => {
    return internalEntries.map((entry) => isInvalidFieldName(entry.name));
  }, [internalEntries, isInvalidFieldName]);

  useEffect(() => {
    if (!isEqual(paramSchema, internalEntries) && !fieldNameErrors.some(Boolean)) {
      updateParamSchema(internalEntries);
    }
  }, [internalEntries, fieldNameErrors, updateParamSchema, paramSchema]);

  const deleteField = (index: number) => {
    setInternalEntries((currentEntries) => [
      ...currentEntries.slice(0, index),
      ...currentEntries.slice(index + 1),
    ]);
  };

  const deleteProperty = (
    fieldIndex: number,
    propertyName: 'options' | 'required_to_show',
    propertyIndex: number,
  ) => {
    setInternalEntries((currentEntries) => {
      const newProperty = [
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...currentEntries[fieldIndex][propertyName].slice(0, propertyIndex),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...currentEntries[fieldIndex][propertyName].slice(propertyIndex + 1),
      ];
      return Object.assign([], currentEntries, {
        [fieldIndex]: {
          ...currentEntries[fieldIndex],
          [propertyName]: newProperty,
        },
      });
    });
  };

  const updateProperty = (index: number, propertyName: string, propertyValue: string | boolean) => {
    setInternalEntries((currentEntries) =>
      Object.assign([], currentEntries, {
        [index]: {
          ...currentEntries[index],
          [propertyName]: propertyValue,
        },
      }),
    );
  };

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const reorderedInputs = reorder(internalEntries, source.index, destination.index);
    setInternalEntries(reorderedInputs);
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='attribute-editor-table__droppable-selected'>
          {(provided) => {
            return (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {internalEntries.map((schema, index) => {
                  return (
                    <DndTitleRow key={index} draggableId={`${schema.name}-${index}`} index={index}>
                      {(draggableProvided) => {
                        return (
                          <ItemConfiguration
                            index={index}
                            schema={schema}
                            fieldNameErrors={fieldNameErrors}
                            internalEntries={internalEntries}
                            updateProperty={updateProperty}
                            deleteField={deleteField}
                            setInternalEntries={setInternalEntries}
                            deleteProperty={deleteProperty}
                            draggableProvided={draggableProvided}
                          />
                        );
                      }}
                    </DndTitleRow>
                  );
                })}
                {provided.placeholder}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
      {internalEntries.length === 0 && <WithLeftSpace>No input fields configured</WithLeftSpace>}
      <Spacer height={2} />
      <WithLeftSpace>
        <Button
          onClick={() => {
            setInternalEntries((currentEntries) => {
              return [
                ...currentEntries,
                {
                  name: `new_field_${currentEntries.length}`,
                  label: '',
                  placeholder: '',
                  prefill: '',
                  optional: false,
                  type: InputSchemaType.Input,
                  options: [],
                  required_to_show: [],
                },
              ];
            });
          }}
          id={`${REACT_SELECT_PORTAL_ID_PREFIX}_add-input`}
          data-test={`${REACT_SELECT_PORTAL_ID_PREFIX}_add-input`}
          type='button'
          variant='inverse'
          width='auto'
          prefixIcon={<PlusIcon />}
        >
          Add input
        </Button>
      </WithLeftSpace>
    </>
  );
};

interface ItemConfigurationProps {
  index: number;
  schema: TriggerCaseParamSchema;
  fieldNameErrors: boolean[];
  internalEntries: TriggerCaseParamSchema[];
  draggableProvided: DraggableProvided;
  updateProperty: (index: number, propertyName: string, propertyValue: string | boolean) => void;
  deleteField: (index: number) => void;
  setInternalEntries: (value: SetStateAction<TriggerCaseParamSchema[]>) => void;
  deleteProperty: (
    fieldIndex: number,
    propertyName: 'options' | 'required_to_show',
    propertyIndex: number,
  ) => void;
}
const ItemConfiguration = ({
  index,
  schema,
  fieldNameErrors,
  internalEntries,
  draggableProvided,
  updateProperty,
  deleteField,
  setInternalEntries,
  deleteProperty,
}: ItemConfigurationProps) => {
  return (
    <Accordion
      dataTest={`${REACT_SELECT_PORTAL_ID_PREFIX}_accordion-${index}`}
      caretSize='md'
      alignCaret='start'
      defaultExpanded={false}
      titleGapSize={`${Spacing[1]}`}
      title={
        <Row>
          <TitleContainer>
            <PropertyInput
              index={index}
              name='name'
              value={schema.name}
              fieldNameErrors={fieldNameErrors}
              updateProperty={updateProperty}
            />
          </TitleContainer>
          <DeleteButton
            id={`${REACT_SELECT_PORTAL_ID_PREFIX}_delete-field-${index}`}
            role='button'
            aria-label={`Delete input field-${index}`}
            tabIndex={0}
            onClick={() => deleteField(index)}
            onKeyPress={(e) => {
              if (['Enter', 'Space'].includes(e.nativeEvent.code)) {
                e.preventDefault();
                deleteField(index);
              }
            }}
          >
            <XIcon />
          </DeleteButton>
        </Row>
      }
    >
      <PropertyContainer>
        <PropertyInput
          index={index}
          label='Label'
          name='label'
          value={schema.label}
          updateProperty={updateProperty}
        />
        <Spacer height={1} />
        <PropertyInput
          index={index}
          label='Prefill'
          name='prefill'
          value={schema.prefill}
          updateProperty={updateProperty}
        />
        <Spacer height={1} />
        <PropertyInput
          index={index}
          label='Placeholder'
          name='placeholder'
          value={schema.placeholder}
          updateProperty={updateProperty}
        />
        <Spacer height={1} />
        <div>
          <Paragraph color='primary'>Type</Paragraph>
          {/*
            Workaround to prevent react-beautiful-dnd from hijacking events from react-select
            https://github.com/atlassian/react-beautiful-dnd/issues/1810#issuecomment-1077952496
           */}
          <div
            data-rbd-drag-handle-context-id={
              draggableProvided.dragHandleProps?.['data-rbd-drag-handle-context-id']
            }
            data-rbd-drag-handle-draggable-id='ignore-drag'
            style={{
              cursor: 'auto',
            }}
          >
            <ReactSelect
              id={`${REACT_SELECT_PORTAL_ID_PREFIX}_type-${index}`}
              aria-label={`Select type for field-${index}`}
              portal={true}
              value={schema.type}
              options={Object.entries(InputSchemaType).map(([key, value]) => ({
                label: startCase(key),
                value: value,
              }))}
              onChange={(newType) => updateProperty(index, 'type', newType)}
            />
          </div>
        </div>
        {/* Options */}
        {[InputSchemaType.Dropdown, InputSchemaType.RadioGroup].includes(schema.type) && (
          <>
            <Spacer height={1} />
            <Accordion
              title={<Paragraph color='primary'>Options</Paragraph>}
              caretSize='md'
              titleGapSize={`${Spacing[1]}`}
              dataTest={`${REACT_SELECT_PORTAL_ID_PREFIX}_accordion-options-${index}`}
              defaultExpanded={true}
            >
              <AccordionContainer>
                {(schema.options || []).map((option, i) => (
                  <>
                    <OptionContainer key={i}>
                      <StyledInput
                        aria-label={`Select option label-${i} for field-${index}`}
                        value={option.label}
                        placeholder='Label'
                        aria-invalid={fieldNameErrors[index]}
                        hasError={fieldNameErrors[index]}
                        onChange={(e) => {
                          setInternalEntries((currentEntries) =>
                            Object.assign([], currentEntries, {
                              [index]: {
                                ...currentEntries[index],
                                options: Object.assign([], currentEntries[index].options, {
                                  [i]: {
                                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                    ...currentEntries[index].options[i],
                                    label: e.target.value,
                                  },
                                }),
                              },
                            }),
                          );
                        }}
                      />
                      <StyledInput
                        aria-label={`Select option value-${i} for field-${index}`}
                        value={option.value}
                        placeholder='Value'
                        aria-invalid={fieldNameErrors[index]}
                        hasError={fieldNameErrors[index]}
                        onChange={(e) => {
                          setInternalEntries((currentEntries) =>
                            Object.assign([], currentEntries, {
                              [index]: {
                                ...currentEntries[index],
                                options: Object.assign([], currentEntries[index].options, {
                                  [i]: {
                                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                    ...currentEntries[index].options[i],
                                    value: e.target.value,
                                  },
                                }),
                              },
                            }),
                          );
                        }}
                      />
                      <DeleteButton
                        id={`${REACT_SELECT_PORTAL_ID_PREFIX}_delete-options-${index}-${i}`}
                        role='button'
                        aria-label='Delete this option'
                        tabIndex={0}
                        onClick={() => {
                          deleteProperty(index, 'options', i);
                        }}
                        onKeyPress={(e) => {
                          if (['Enter', 'Space'].includes(e.nativeEvent.code)) {
                            e.preventDefault();
                            deleteProperty(index, 'options', i);
                          }
                        }}
                      >
                        <XIcon />
                      </DeleteButton>
                    </OptionContainer>
                    <Spacer height={1} />
                  </>
                ))}
                <Button
                  onClick={() => {
                    setInternalEntries((currentEntries) => {
                      const newOptions = [
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        ...currentEntries[index].options,
                        {
                          label: '',
                          value: '',
                        },
                      ];
                      return Object.assign([], currentEntries, {
                        [index]: {
                          ...currentEntries[index],
                          options: newOptions,
                        },
                      });
                    });
                  }}
                  id={`${REACT_SELECT_PORTAL_ID_PREFIX}_add-options-${index}`}
                  data-test={`${REACT_SELECT_PORTAL_ID_PREFIX}_add-options-${index}`}
                  type='button'
                  variant='inverse'
                  width='auto'
                  prefixIcon={<PlusIcon />}
                >
                  Add option
                </Button>
              </AccordionContainer>
            </Accordion>
          </>
        )}

        <Spacer height={1} />
        {/* Requirements */}
        <Accordion
          caretSize='md'
          title={<Paragraph color='primary'>Requirements</Paragraph>}
          titleGapSize={`${Spacing[1]}`}
          dataTest={`${REACT_SELECT_PORTAL_ID_PREFIX}_accordion-requirements-${index}`}
          defaultExpanded={true}
        >
          <AccordionContainer>
            {(schema.required_to_show || []).map((req, i) => (
              <RequirementContainer key={i}>
                <Row>
                  <Paragraph color='primary'>{i === 0 ? 'Where' : 'And'}</Paragraph>
                  {/*
                  Workaround to prevent react-beautiful-dnd from hijacking events from react-select
                  https://github.com/atlassian/react-beautiful-dnd/issues/1810#issuecomment-1077952496
                 */}
                  <div
                    data-rbd-drag-handle-context-id={
                      draggableProvided.dragHandleProps?.['data-rbd-drag-handle-context-id']
                    }
                    data-rbd-drag-handle-draggable-id='ignore-drag'
                    style={{
                      cursor: 'auto',
                    }}
                  >
                    <ReactSelect
                      id={`${REACT_SELECT_PORTAL_ID_PREFIX}_requirements_name-${index}-${i}`}
                      aria-label={`Select requirement name-${i} for field-${index}`}
                      portal={true}
                      value={req.name}
                      options={internalEntries
                        .filter((_, idx) => idx !== index)
                        .map((entry) => {
                          // exclude field itself as its display dependency
                          return {
                            label: entry.name,
                            value: entry.name,
                          };
                        })}
                      onChange={(newName) => {
                        setInternalEntries((currentEntries) => {
                          const newRequirement = Object.assign(
                            [],
                            currentEntries[index].required_to_show,
                            {
                              [i]: {
                                name: newName,
                                values: [],
                              },
                            },
                          );
                          return Object.assign([], currentEntries, {
                            [index]: {
                              ...currentEntries[index],
                              required_to_show: newRequirement,
                            },
                          });
                        });
                      }}
                    />
                  </div>
                </Row>
                <Spacer height={1} />
                <Row>
                  <StyledParagraph size='sm' color='primary'>
                    is in
                  </StyledParagraph>
                  {/*
                  Workaround to prevent react-beautiful-dnd from hijacking events from react-select
                  https://github.com/atlassian/react-beautiful-dnd/issues/1810#issuecomment-1077952496
                 */}
                  <div
                    data-rbd-drag-handle-context-id={
                      draggableProvided.dragHandleProps?.['data-rbd-drag-handle-context-id']
                    }
                    data-rbd-drag-handle-draggable-id='ignore-drag'
                    style={{
                      cursor: 'auto',
                    }}
                  >
                    <RequirementValueSelect
                      id={`${REACT_SELECT_PORTAL_ID_PREFIX}_requirements_values-${index}-${i}`}
                      label={`Select requirement values-${i} for field-${index}`}
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      type={internalEntries.find((entry) => entry.name === req.name)?.type}
                      value={req.values}
                      options={internalEntries.find((entry) => entry.name === req.name)?.options}
                      onChange={(newValues) => {
                        setInternalEntries((currentEntries) => {
                          const newRequiredToShow = Object.assign(
                            [],
                            currentEntries[index].required_to_show,
                            {
                              [i]: {
                                ...currentEntries[index].required_to_show[i],
                                values: newValues,
                              },
                            },
                          );
                          return Object.assign([], currentEntries, {
                            [index]: {
                              ...currentEntries[index],
                              required_to_show: newRequiredToShow,
                            },
                          });
                        });
                      }}
                    />
                  </div>
                </Row>
                <DeleteButton
                  id={`${REACT_SELECT_PORTAL_ID_PREFIX}_delete-requirement`}
                  role='button'
                  aria-label='Delete this requirement'
                  tabIndex={0}
                  onClick={() => deleteProperty(index, 'required_to_show', i)}
                  onKeyPress={(e) => {
                    if (['Enter', 'Space'].includes(e.nativeEvent.code)) {
                      e.preventDefault();
                      deleteProperty(index, 'required_to_show', i);
                    }
                  }}
                >
                  <XIcon />
                </DeleteButton>
              </RequirementContainer>
            ))}
            <Button
              onClick={() => {
                setInternalEntries((currentEntries) =>
                  Object.assign([], currentEntries, {
                    [index]: {
                      ...currentEntries[index],
                      required_to_show: [
                        ...currentEntries[index].required_to_show,
                        { name: '', values: [] },
                      ],
                    },
                  }),
                );
              }}
              id={`${REACT_SELECT_PORTAL_ID_PREFIX}_add-requirement`}
              data-test={`${REACT_SELECT_PORTAL_ID_PREFIX}_add-requirement`}
              type='button'
              variant='inverse'
              width='auto'
              prefixIcon={schema.required_to_show.length === 0 ? <PlusIcon /> : undefined}
            >
              {schema.required_to_show.length === 0 ? 'Add requirement' : 'And'}
            </Button>
          </AccordionContainer>
        </Accordion>
        <Spacer height={1} />
        <BooleanParamContainer>
          <CheckboxContainer
            id={`${REACT_SELECT_PORTAL_ID_PREFIX}_optional-container-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              updateProperty(index, 'optional', !schema.optional);
            }}
          >
            <Checkbox
              id={`${REACT_SELECT_PORTAL_ID_PREFIX}_optional-${index}`}
              aria-label={`Select optional for field-${index}`}
              checked={schema.optional}
            />
            <Paragraph color='primary'>Mark input completion as optional</Paragraph>
          </CheckboxContainer>
          <Spacer height={1} />
          <CheckboxContainer
            id={`${REACT_SELECT_PORTAL_ID_PREFIX}_disabled-container-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              updateProperty(index, 'disabled', !schema.disabled);
            }}
          >
            <Checkbox
              id={`${REACT_SELECT_PORTAL_ID_PREFIX}_disabled-${index}`}
              aria-label={`Select disabled for field-${index}`}
              checked={schema.disabled}
            />
            <Paragraph color='primary'>Mark input as disabled</Paragraph>
          </CheckboxContainer>
        </BooleanParamContainer>
      </PropertyContainer>
    </Accordion>
  );
};

const DndWrapper = styled('div')`
  width: 100%;
  padding: ${Spacing[1]} 0;

  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
`;

interface DndTitleRowProps {
  draggableId: string;
  index: number;
  children: ReactNode | DraggableChildrenFn;
}
const DndTitleRow = ({ draggableId, index, children }: DndTitleRowProps) => {
  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot, rubric) => {
        return (
          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <DndWrapper>
              <DragIcon />
              {typeof children === 'function' ? children(provided, snapshot, rubric) : children}
            </DndWrapper>
          </div>
        );
      }}
    </Draggable>
  );
};

interface RequirementValueSelectProps {
  id: string;
  label: string;
  type: InputSchemaType;
  value: string[];
  onChange: (value: string) => void;
  options?: { label: string; value: string }[];
}

const RequirementValueSelect: VFC<RequirementValueSelectProps> = ({
  id,
  label,
  type,
  value,
  options,
  onChange,
}) => {
  switch (type) {
    case InputSchemaType.Dropdown:
      return (
        <ReactSelect
          id={id}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          styles={ReactSelectStyles}
          aria-label={label}
          portal={true}
          isMulti={true}
          isClearable={true}
          value={value}
          options={options || []}
          onChange={onChange}
        />
      );
    case InputSchemaType.RadioGroup:
      return (
        <ReactSelect
          id={id}
          aria-label={label}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          styles={ReactSelectStyles}
          portal={true}
          isMulti={true}
          isClearable={true}
          value={value}
          options={options || []}
          onChange={onChange}
        />
      );
    case InputSchemaType.Country:
      return (
        <ReactSelect
          id={id}
          aria-label={label}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          styles={ReactSelectStyles}
          portal={true}
          isMulti={true}
          isClearable={true}
          value={value}
          options={sortBy(
            'label',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            Object.keys(COUNTRIES).map((countryCode: CountryCode) => ({
              value: countryCode,
              label: COUNTRIES[countryCode],
            })),
          )}
          onChange={onChange}
        />
      );
    case InputSchemaType.USState:
      return (
        <ReactSelect
          id={`${REACT_SELECT_PORTAL_ID_PREFIX}_requirements_values-${type}`}
          aria-label={`Select requirement values for ${type}`}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          styles={ReactSelectStyles}
          portal={true}
          isMulti={true}
          isClearable={true}
          value={value}
          options={sortBy(
            'label',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            Object.keys(US_STATES).map((code: keyof typeof US_STATES) => ({
              label: US_STATES[code],
              value: US_STATES[code],
            })),
          )}
          onChange={onChange}
        />
      );
    case InputSchemaType.USStateCode:
      return (
        <ReactSelect
          id={`${REACT_SELECT_PORTAL_ID_PREFIX}_requirements_values-${type}`}
          aria-label={`Select requirement values for ${type}`}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          styles={ReactSelectStyles}
          value={value}
          portal={true}
          isMulti={true}
          isClearable={true}
          options={sortBy(
            'label',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            Object.keys(US_STATES).map((code: keyof typeof US_STATES) => ({
              label: US_STATES[code],
              value: code,
            })),
          )}
          onChange={onChange}
        />
      );
    default: {
      return (
        <ReactCreatableSelect
          id={id}
          aria-label={label}
          value={value}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          styles={ReactSelectStyles}
          options={value.map((val) => ({ label: val, value: val }))}
          onChange={onChange}
          menuPlacement='bottom'
          hideSelectedOptions={false}
          isSearchable={true}
          isMulti={true}
          portal={true}
        />
      );
    }
  }
};

export default TriggerCaseParamSchemaEditor;
